import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import { useHistory } from 'react-router';
import { OutpostDestination, OutpostDestinationTypeMetadata, OutpostDestinationUpdateInput } from '../../../../../../../typings/outpost';
import Button from '../../../common/base/Button';
import Container from '../../../common/base/Container';
import Divider from '../../../common/base/Divider';
import Link from '../../../common/base/Link';
import Loading from '../../../common/base/Loading';
import Text from '../../../common/base/Text';
import { StyledCard } from '../../../common/base/Card';
import { useDialog } from '../../../common/Dialog';
import EditorInput from '../../../common/Form/Fields/EditorInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { TeamPermission } from '../../../contexts/TeamPermissionContext';
import LINKS from '../../../../configs/links';
import { useOutpostConfig } from '../OutpostContext';
import { DestinationConfigFields, DestinationConfigFormValues, DestinationTopicsFields, DestinationTopicsFormValues, getDestinationInitialValues, getDestinationTopicsInitialValues, getTopicOptions, serializeDestinationInput } from '../utils/outpostForms';
interface FilterFormValues {
  filter: string;
}
const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const response = error.response;
  if (!response || typeof response !== 'object' || !('message' in response)) {
    return undefined;
  }
  return typeof response.message === 'string' ? response.message : undefined;
};
export const DestinationSettings = ({
  tenant_id,
  destination,
  destination_type,
  mutate
}: {
  tenant_id: string;
  destination: OutpostDestination;
  destination_type?: OutpostDestinationTypeMetadata;
  mutate: (data: OutpostDestination) => void;
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const history = useHistory();
  const {
    config
  } = useOutpostConfig();
  const [is_submitting_topics, setIsSubmittingTopics] = useState(false);
  const [is_submitting_config, setIsSubmittingConfig] = useState(false);
  const [editing_fields, setEditingFields] = useState<Record<string, boolean>>({});
  const [is_filter_enabled, setIsFilterEnabled] = useState(!!(destination.filter && Object.keys(destination.filter).length > 0));
  const [is_submitting_filter, setIsSubmittingFilter] = useState(false);
  const [is_rotating_secret, setIsRotatingSecret] = useState(false);
  const topic_options = getTopicOptions(config['TOPICS'] ?? undefined);
  const topics_initial_values = getDestinationTopicsInitialValues(destination.topics);
  const config_initial_values = getDestinationInitialValues(destination_type, destination);
  const filter_initial_values: FilterFormValues = (() => {
    const raw = destination.filter;
    const has_raw = raw && typeof raw === 'object' && Object.keys(raw).length > 0;
    const obj: Record<string, unknown> = has_raw ? {
      ...raw
    } : {
      data: {},
      metadata: {}
    };
    return {
      filter: JSON.stringify(obj, null, 2)
    };
  })();
  const onSubmitTopics = async (values: DestinationTopicsFormValues) => {
    setIsSubmittingTopics(true);
    try {
      const updated = await HookdeckAPI.outpost.destinations.update(tenant_id, destination.id, {
        topics: values.all_topics ? ['*'] : values.topics
      });
      mutate(updated);
      addToast('success', 'Topics updated');
    } catch (error: unknown) {
      const error_message = getErrorMessage(error);
      addToast('error', `An error occurred while updating topics${error_message ? `: ${error_message}` : ''}`);
    } finally {
      setIsSubmittingTopics(false);
    }
  };
  const onSubmitConfig = async (values_0: DestinationConfigFormValues) => {
    setIsSubmittingConfig(true);
    try {
      const update_data = serializeDestinationInput({
        destination_type,
        values: values_0,
        editing_fields,
        include_type: false
      }) as OutpostDestinationUpdateInput;
      const updated_0 = await HookdeckAPI.outpost.destinations.update(tenant_id, destination.id, update_data);
      mutate(updated_0);
      setEditingFields({});
      addToast('success', 'Configuration updated');
    } catch (error_0: unknown) {
      const error_message_0 = getErrorMessage(error_0);
      addToast('error', `An error occurred while updating the configuration${error_message_0 ? `: ${error_message_0}` : ''}`);
    } finally {
      setIsSubmittingConfig(false);
    }
  };
  const onSubmitFilter = async (values_1: FilterFormValues) => {
    setIsSubmittingFilter(true);
    try {
      let filter_data: Record<string, unknown> = {};
      try {
        filter_data = JSON.parse(values_1.filter);
      } catch {
        addToast('error', 'Invalid JSON in filter');
        setIsSubmittingFilter(false);
        return;
      }
      const is_non_empty_object = (v: unknown) => v != null && typeof v === 'object' && !Array.isArray(v) && Object.keys(v as Record<string, unknown>).length > 0;
      const trimmed: Record<string, unknown> = {};
      if (is_non_empty_object(filter_data.data)) trimmed.data = filter_data.data;
      if (is_non_empty_object(filter_data.metadata)) trimmed.metadata = filter_data.metadata;
      const has_filter = Object.keys(trimmed).length > 0;
      const updated_1 = await HookdeckAPI.outpost.destinations.update(tenant_id, destination.id, {
        filter: has_filter ? trimmed : {}
      });
      mutate(updated_1);
      addToast('success', 'Filter updated');
    } catch (error_1: unknown) {
      const error_message_1 = getErrorMessage(error_1);
      addToast('error', `An error occurred while updating the filter${error_message_1 ? `: ${error_message_1}` : ''}`);
    } finally {
      setIsSubmittingFilter(false);
    }
  };
  const onRemoveFilter = async () => {
    setIsSubmittingFilter(true);
    try {
      const updated_2 = await HookdeckAPI.outpost.destinations.update(tenant_id, destination.id, {
        filter: {}
      });
      mutate(updated_2);
      setIsFilterEnabled(false);
      addToast('success', 'Filter removed');
    } catch (error_2: unknown) {
      const error_message_2 = getErrorMessage(error_2);
      addToast('error', `An error occurred while removing the filter${error_message_2 ? `: ${error_message_2}` : ''}`);
    } finally {
      setIsSubmittingFilter(false);
    }
  };
  const onRotateSecret = () => {
    showDialog(async () => {
      setIsRotatingSecret(true);
      try {
        const updated_3 = await HookdeckAPI.outpost.destinations.update(tenant_id, destination.id, {
          credentials: {
            rotate_secret: true
          }
        });
        mutate(updated_3);
        addToast('success', 'Secret rotated successfully. The previous secret will be invalidated after 24 hours.');
      } catch (error_3: unknown) {
        const error_message_3 = getErrorMessage(error_3);
        addToast('error', `An error occurred while rotating the secret${error_message_3 ? `: ${error_message_3}` : ''}`);
      } finally {
        setIsRotatingSecret(false);
      }
    }, () => null, {
      title: 'Rotate secret',
      submit_label: 'Rotate',
      cancel_label: 'Cancel',
      message: 'This will generate a new signing secret. The current secret will remain valid for 24 hours. Are you sure?'
    });
  };
  const onToggleEnabled = async () => {
    try {
      const updated_4 = destination.disabled_at ? await HookdeckAPI.outpost.destinations.enable(tenant_id, destination.id) : await HookdeckAPI.outpost.destinations.disable(tenant_id, destination.id);
      mutate(updated_4);
      addToast('success', destination.disabled_at ? 'Destination enabled' : 'Destination disabled');
    } catch {
      addToast('error', 'Failed to update destination status');
    }
  };
  const onDelete = () => {
    showDialog(() => {
      HookdeckAPI.outpost.destinations.delete(tenant_id, destination.id).then(() => {
        addToast('success', 'Destination deleted');
        history.push(`/tenants/${tenant_id}`);
      }).catch(() => {
        addToast('error', 'Failed to delete destination');
      });
    }, () => null, {
      danger: true,
      title: 'Delete destination',
      submit_label: 'Delete',
      cancel_label: 'Keep destination',
      message: 'By deleting this destination, all associated deliveries will also be deleted. This action cannot be undone. Are you sure?'
    });
  };
  return <Div>
      <Container medium left>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={{
          t: 6,
          b: 0
        }}>
            Topics
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Manage which topics this destination subscribes to.
          </Text>
          <Formik<DestinationTopicsFormValues> initialValues={topics_initial_values} enableReinitialize onSubmit={onSubmitTopics}>
            {({
            dirty,
            values: values_2
          }) => <Form>
                <DestinationTopicsFields topic_options={topic_options} form_values={values_2} />
                <Div m={{
              t: 6
            }} flex={{
              gap: 4,
              align: 'center'
            }}>
                  <Button.Permission primary disabled={is_submitting_topics || !dirty} submit icon={is_submitting_topics ? 'loading' : 'save'}>
                    Save
                  </Button.Permission>
                  {dirty && <Text muted size="m">
                      You have unsaved changes
                    </Text>}
                </Div>
              </Form>}
          </Formik>
        </TeamPermission>
      </Container>
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={0}>
            Configuration
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Update configuration and credentials for this destination.
          </Text>
          {destination_type ? <Formik<DestinationConfigFormValues> initialValues={config_initial_values} enableReinitialize onSubmit={onSubmitConfig}>
              {({
            dirty: dirty_0
          }) => <Form>
                  <DestinationConfigFields destination_type={destination_type} mode="edit" editing_fields={editing_fields} setEditingFields={setEditingFields} destination={destination} />
                  <Div m={{
              t: 4
            }} flex={{
              gap: 4,
              align: 'center'
            }}>
                    <Button.Permission primary disabled={is_submitting_config || !dirty_0} submit icon={is_submitting_config ? 'loading' : 'save'}>
                      Save
                    </Button.Permission>
                    {dirty_0 && <Text muted size="m">
                        You have unsaved changes
                      </Text>}
                  </Div>
                </Form>}
            </Formik> : <Loading />}
        </TeamPermission>
      </Container>
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={0}>
            Event filter
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Filter events based on their data or metadata. Only events matching the filter will be
            delivered.{' '}
            <Link href={LINKS.outpost.filters} target="_blank" icon="link">
              View documentation
            </Link>
          </Text>
          {is_filter_enabled ? <Formik<FilterFormValues> initialValues={filter_initial_values} enableReinitialize validate={values_3 => {
          const errors: Partial<Record<keyof FilterFormValues, string>> = {};
          const raw_0 = values_3.filter?.trim() ?? '';
          if (raw_0) {
            try {
              const parsed = JSON.parse(raw_0);
              if (typeof parsed !== 'object' || Array.isArray(parsed)) {
                errors.filter = 'Filter must be a JSON object with "data" and/or "metadata" keys';
              } else {
                const top_level = Object.keys(parsed);
                const invalid = top_level.filter(k => k !== 'data' && k !== 'metadata');
                if (invalid.length > 0) {
                  errors.filter = 'Filter may only contain top-level "data" and "metadata" keys';
                } else if (!top_level.includes('data') && !top_level.includes('metadata')) {
                  errors.filter = 'Filter must include at least one of "data" or "metadata" at the top level';
                } else {
                  const data_empty = !parsed.data || typeof parsed.data !== 'object' || Array.isArray(parsed.data) || Object.keys(parsed.data).length === 0;
                  const metadata_empty = !parsed.metadata || typeof parsed.metadata !== 'object' || Array.isArray(parsed.metadata) || Object.keys(parsed.metadata).length === 0;
                  if (data_empty && metadata_empty) {
                    errors.filter = 'At least one of "data" or "metadata" must be a non-empty object';
                  }
                }
              }
            } catch {
              errors.filter = 'Invalid JSON';
            }
          }
          return errors;
        }} onSubmit={onSubmitFilter}>
              {({
            dirty: dirty_1,
            errors: errors_0
          }) => <Form>
                  <StyledCard overflow_hidden m={{
              b: 4
            }}>
                    <EditorInput name="filter" language="json" format_on_blur display="minimal" height="200px" />
                  </StyledCard>
                  <Div flex={{
              gap: 4,
              align: 'center'
            }}>
                    <Button.Permission primary disabled={is_submitting_filter || !dirty_1 || !!errors_0.filter} submit icon={is_submitting_filter ? 'loading' : 'save'}>
                      Save
                    </Button.Permission>
                    <Button.Permission outline disabled={is_submitting_filter} onClick={onRemoveFilter} icon={is_submitting_filter ? 'loading' : 'close'}>
                      Remove filter
                    </Button.Permission>
                    {dirty_1 && <Text muted size="m">
                        You have unsaved changes
                      </Text>}
                  </Div>
                </Form>}
            </Formik> : <Button.Permission outline onClick={() => setIsFilterEnabled(true)} icon="filter_list">
              Add filter
            </Button.Permission>}
        </TeamPermission>
      </Container>
      {destination.type === 'webhook' && <>
          <Div m={{
        x: 6,
        t: 10,
        b: 8
      }}>
            <Divider />
          </Div>
          <Container medium left>
            <TeamPermission role="member">
              <Text as="h2" size="l" bold m={0}>
                Rotate Secret
              </Text>
              <Text as="p" muted m={{
            b: 4
          }}>
                Rotate the destination signing secret. This will generate a new secret and
                invalidate the current one after 24 hours.
              </Text>
              <Button.Permission outline onClick={onRotateSecret} icon={is_rotating_secret ? 'loading' : 'reload'} disabled={is_rotating_secret}>
                Rotate
              </Button.Permission>
            </TeamPermission>
          </Container>
        </>}
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={0}>
            {destination.disabled_at ? 'Enable destination' : 'Disable destination'}
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            {destination.disabled_at ? 'Re-enable this destination to resume event deliveries.' : 'Disable this destination to temporarily stop event deliveries. You can re-enable it later.'}
          </Text>
          {destination.disabled_at ? <Button.Permission primary outline onClick={onToggleEnabled} icon="enable">
              Enable destination
            </Button.Permission> : <Button.Permission danger outline onClick={onToggleEnabled} icon="disable">
              Disable destination
            </Button.Permission>}
        </TeamPermission>
      </Container>
      <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}>
        <Divider />
      </Div>
      <Container medium left m={{
      b: 20
    }}>
        <TeamPermission role="member">
          <Text as="h2" size="l" bold m={0}>
            Delete destination
          </Text>
          <Text as="p" muted m={{
          b: 4
        }}>
            Permanently delete this destination and all associated delivery data.
          </Text>
          <Button.Permission danger outline onClick={onDelete} icon="delete">
            Delete destination
          </Button.Permission>
        </TeamPermission>
      </Container>
    </Div>;
};